import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { heroImages } from "../data/images";

export const HeroSlider = () => {
  const { t } = useLang();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 35 });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
    const id = setInterval(() => emblaApi.scrollNext(), 6500);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const slides = t("hero.slides") || [];

  return (
    <section id="top" className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-[#1A1A1A]" data-testid="hero-section">
      {/* Embla */}
      <div className="embla absolute inset-0" ref={emblaRef}>
        <div className="embla__container h-full">
          {heroImages.map((src, i) => (
            <div className="embla__slide h-full" key={src}>
              <motion.div
                key={`${src}-${selectedIndex === i}`}
                initial={{ scale: 1.08 }}
                animate={{ scale: selectedIndex === i ? 1.02 : 1.08 }}
                transition={{ duration: 7, ease: "easeOut" }}
                className="h-full w-full will-change-transform"
              >
                <img
                  src={src}
                  alt={`Pick Bodenbeläge — ${slides[i % slides.length]?.tag || ""}`}
                  className="w-full h-full object-cover"
                  loading={i === 0 ? "eager" : "lazy"}
                  draggable={false}
                />
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/65 pointer-events-none" />
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      {/* Top content (eyebrow + nav already absolute) */}
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex-1" />

        {/* Bottom-left text block */}
        <div className="px-6 lg:px-12 pb-12 lg:pb-20 max-w-[1400px] w-full mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-xs font-bold uppercase tracking-[0.28em] text-[#F9F8F6]/85 mb-6"
            data-testid="hero-eyebrow"
          >
            {t("hero.eyebrow")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-white text-5xl sm:text-7xl lg:text-[8rem] leading-[0.92] font-light tracking-tighter max-w-5xl"
            data-testid="hero-title"
          >
            {t("hero.title_l1")}
            <br />
            <span className="italic font-extralight">{t("hero.title_l2")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="mt-8 max-w-xl text-base lg:text-lg text-white/85 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              className="btn-primary"
              style={{ background: "#F9F8F6", color: "#1A1A1A" }}
              data-testid="hero-cta-primary"
            >
              {t("hero.cta_primary")}
            </Link>
            <Link
              to="/products"
              className="btn-ghost"
              style={{ borderColor: "#F9F8F6", color: "#F9F8F6" }}
              data-testid="hero-cta-secondary"
            >
              {t("hero.cta_secondary")}
            </Link>
          </motion.div>

          {/* Slide info + controls row */}
          <div className="mt-14 flex items-end justify-between gap-6 border-t border-white/20 pt-6">
            <AnimatePresence mode="wait">
              {slides[selectedIndex] && (
                <motion.div
                  key={selectedIndex}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.5 }}
                  className="text-white/90"
                  data-testid="hero-slide-info"
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">
                    {String(selectedIndex + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")} · {slides[selectedIndex].tag}
                  </div>
                  <div className="font-display text-2xl lg:text-3xl mt-1">{slides[selectedIndex].title}</div>
                  <div className="text-sm opacity-75 mt-1">{slides[selectedIndex].caption}</div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => emblaApi?.scrollPrev()}
                className="h-12 w-12 rounded-full border border-white/40 text-white grid place-items-center hover:bg-white hover:text-[#1A1A1A] transition-colors"
                aria-label="Previous slide"
                data-testid="hero-prev-btn"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => emblaApi?.scrollNext()}
                className="h-12 w-12 rounded-full border border-white/40 text-white grid place-items-center hover:bg-white hover:text-[#1A1A1A] transition-colors"
                aria-label="Next slide"
                data-testid="hero-next-btn"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute right-6 lg:right-12 bottom-6 hidden md:flex items-center gap-3 text-white/70 text-xs tracking-[0.2em] uppercase">
        <span>{t("hero.scroll")}</span>
        <ArrowDown size={14} className="animate-nudge" />
      </div>
    </section>
  );
};
